import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, MapPin, Hammer, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CtaBand } from "@/components/CtaBand";
import { PROJECTS } from "@/lib/data";

const FILTERS = [
  { id: "all", label: "Tous", testId: "realisations-filter-all" },
  { id: "couverture", label: "Couverture", testId: "realisations-filter-couverture" },
  { id: "etancheite", label: "Étanchéité", testId: "realisations-filter-etancheite" },
  { id: "renovation", label: "Rénovation", testId: "realisations-filter-renovation" },
];

export default function Realisations() {
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const visible = filter === "all" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <main data-testid="realisations-page">
      <Seo
        title="Réalisations — LE ROCH"
        siteName="LE ROCH"
        description="Réalisations Le Roch Couverture : église de Guerlédan en ardoise, office notarial à Pontivy, bardage et étanchéité à Saint-Allouestre — Morbihan et Côtes d'Armor."
      />
      <PageHero
        kicker="Réalisations"
        lines={["DES CHANTIERS QUI", "PARLENT D'EUX-MÊMES."]}
        accentIndex={1}
        description="Chaque projet est documenté, photographié et garanti. Explorez une sélection de nos toitures livrées ces dernières années."
      />

      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex flex-wrap gap-3" data-testid="realisations-filters">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  data-testid={f.testId}
                  onClick={() => setFilter(f.id)}
                  className={`rounded-full px-5 py-2.5 font-display text-sm font-bold transition-all duration-300 ${
                    filter === f.id
                      ? "bg-roch-navy text-white"
                      : "border border-slate-300 bg-white text-slate-600 hover:border-roch-navy hover:text-roch-navy"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </Reveal>

          <motion.div layout className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            <AnimatePresence mode="popLayout">
              {visible.map((p) => (
                <motion.article
                  layout
                  key={p.id}
                  initial={{ opacity: 0, scale: 0.94 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.94 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                >
                  <button
                    onClick={() => setSelected(p)}
                    data-testid={`project-card-${p.id}`}
                    className="group block w-full text-left"
                  >
                    <div className="relative overflow-hidden rounded-3xl">
                      <img
                        src={p.image}
                        alt={`${p.title} — ${p.work}`}
                        loading="lazy"
                        className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-roch-navy/90 via-roch-navy/10 to-transparent" />
                      <span className="absolute left-5 top-5 rounded-full bg-roch-orange px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-roch-navy">
                        {FILTERS.find((f) => f.id === p.category)?.label}
                      </span>
                      <div className="absolute inset-x-0 bottom-0 p-6">
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-roch-orange">{p.location}</p>
                        <h3 className="mt-2 font-display text-xl font-bold text-white">{p.title}</h3>
                        <p className="mt-1 text-sm text-white/60">{p.work}</p>
                      </div>
                    </div>
                  </button>
                </motion.article>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="project-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-roch-navy/80 p-4 backdrop-blur-sm"
            onClick={() => setSelected(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.96 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-3xl overflow-hidden rounded-3xl bg-white"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="project-modal-close"
                onClick={() => setSelected(null)}
                className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-roch-navy/80 text-white backdrop-blur transition-colors hover:bg-roch-orange hover:text-roch-navy"
                aria-label="Fermer"
              >
                <X className="h-5 w-5" />
              </button>
              <img src={selected.image} alt={`${selected.title} — ${selected.work}`} className="aspect-[16/9] w-full object-cover" />
              <div className="p-8 sm:p-10">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-roch-orange">
                  {FILTERS.find((f) => f.id === selected.category)?.label}
                </p>
                <h3 className="mt-3 font-display text-2xl font-extrabold text-roch-navy sm:text-3xl">{selected.title}</h3>
                <p className="mt-2 text-base text-slate-600">{selected.work}</p>
                <div className="mt-7 grid grid-cols-3 gap-4">
                  {[
                    { icon: MapPin, label: "Lieu", value: selected.location },
                    { icon: Hammer, label: "Travaux", value: selected.work },
                    { icon: ShieldCheck, label: "Garantie", value: "Décennale AXA" },
                  ].map((s) => (
                    <div key={s.label} className="rounded-2xl bg-roch-mist p-4">
                      <s.icon className="h-4 w-4 text-roch-orange" />
                      <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">{s.label}</p>
                      <p className="mt-1 font-display text-sm font-bold text-roch-navy sm:text-base">{s.value}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-6 flex items-center gap-2 text-xs text-slate-500">
                  <Hammer className="h-4 w-4 text-roch-orange" />
                  {selected.detail}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <CtaBand />
    </main>
  );
}
